import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Avatar, InputAdornment,
  TablePagination,
} from '@mui/material';
import { Add, Refresh, ArrowBack, Search, Visibility, Delete, EmojiEvents, PictureAsPdf, Download } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import AsyncStorage from '../../services/AsyncStorage';

export default function Results() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('list');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedExam, setSelectedExam] = useState('');
  const [rank, setRank] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [obtainedMarks, setObtainedMarks] = useState('');
  const [percentage, setPercentage] = useState('');
  const [remarks, setRemarks] = useState('');
  const [subjectsData, setSubjectsData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchStudent, setSearchStudent] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultDetail, setResultDetail] = useState([]);
  const [resultDetailLoading, setResultDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterExam, setFilterExam] = useState('All'); // exam filter

  // ── Generate progress report PDF ──────────────────────────────────────────
  const openProgressReport = async (type, studentId = null) => {
    const token = await AsyncStorage.getItem('token');
    const acYearParam = selected?.acYear ? `?ac_year=${selected.acYear}` : '';
    let url = '';
    if (type === 'single' && studentId) {
      url = `${Config.main_url}/generateProgressReport/${studentId}${acYearParam}`;
    } else if (type === 'section') {
      url = `${Config.main_url}/generateProgressReportBulk/${encodeURIComponent(selected.standard)}/${encodeURIComponent(selected.section)}${acYearParam}`;
    } else if (type === 'all') {
      url = `${Config.main_url}/generateProgressReportBulkAll${acYearParam}`;
    }
    if (!url) return;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Failed to generate report'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
  };

  const loadResults = useCallback(async (cls) => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getResultByStdSecAcYear, {
      acYear: cls.acYear,
      standard: cls.standard,
      section: cls.section,
    });
    setResults(data?.result || []);
    setLoading(false);
  }, []);

  const loadAddData = useCallback(async (cls) => {
    setLoading(true);
    const [studData, subData, examData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getStudentsByStdSecAcYear, {
        acYear: cls.acYear, standard: cls.standard, section: cls.section,
      }),
      InvokeApi(Config.main_url + apiList.getSubjects, {
        standard: cls.standard, section: cls.section,
      }),
      InvokeApi(Config.main_url + apiList.getExams, {
        standard: cls.standard, section: cls.section,
      }),
    ]);
    setStudents(studData?.students || []);
    setSubjects(subData?.subjects || []);
    setExams(examData?.exams || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (subjects.length > 0) {
      setSubjectsData(subjects.map(s => ({
        subject: s.name,
        max_marks: '', min_marks: '', obt_marks: '',
        internals: '', status: '', remarks: '',
      })));
    }
  }, [subjects]);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    setMode('list');
    setFilterExam('All');
    loadResults(cls);
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === 'add' && selected) loadAddData(selected);
    if (m === 'list' && selected) loadResults(selected);
  };

  const totalMaxMarks = subjectsData.reduce(
    (sum, item) => sum + (Number(item.max_marks) || 0),
    0
  );
  const totalObtMarks = subjectsData.reduce(
    (sum, item) => sum + (Number(item.obt_marks) || 0),
    0
  );
  const percentageMain =
    totalMaxMarks > 0
      ? ((totalObtMarks / totalMaxMarks) * 100).toFixed(2)
      : "0.00";

  const handleSave = async () => {
    if (!selectedStudent || !selectedExam) {
      alert('Select student and exam type'); return;
    }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addResult, {
      studentId: selectedStudent.ID,
      examType: selectedExam,
      remarks,
      rank,
      percentage: percentageMain,
      ac_year: selected.acYear,
      subjects: subjectsData,
    });
    setSaving(false);
    if (data?.code === '200') {
      setSelectedStudent(null); setSelectedExam('');
      setRank(''); setPercentage(''); setRemarks('');
      alert('Result added successfully!');
      switchMode('list');
    } else alert(data?.message || 'Failed');
  };

  const updateSubject = (idx, field, val) => {
    setSubjectsData(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: val };
      return updated;
    });
  };

  const openResultDetail = async (r) => {
    setSelectedResult(r);
    setResultDetail([]);
    setResultDetailLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getResults, { result_id: r.ID });
    setResultDetail(data?.results || []);
    setResultDetailLoading(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete result for ${r.full_name || 'this student'} (${r.exam_type})?`)) return;
    setDeletingId(r.ID);
    const data = await InvokeApi(Config.main_url + apiList.deleteResultFull, {
      result_id: r.ID,
    });
    setDeletingId(null);
    if (data?.code === '200') {
      loadResults(selected);
    } else {
      alert(data?.message || 'Failed to delete result');
    }
  };

  const filteredStudents = students.filter(s =>
    !searchStudent || s.full_name?.toLowerCase().includes(searchStudent.toLowerCase())
  );

  // Get unique exam types from loaded results
  const examTypes = ['All', ...Array.from(new Set(results.map(r => r.exam_type).filter(Boolean)))];

  // Filter results by selected exam type
  const filteredResults = filterExam === 'All'
    ? results
    : results.filter(r => r.exam_type === filterExam);

  const paginated = filteredResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Results</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to view results</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Results</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {selected?.acYear}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          {['list', 'add'].map(m => (
            <Button key={m} variant={mode === m ? 'contained' : 'outlined'} onClick={() => switchMode(m)}
              sx={mode === m
                ? { background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }
                : { borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>
              {m === 'list' ? 'Result List' : 'Add Result'}
            </Button>
          ))}
          {mode === 'list' && (
            <>
              <Tooltip title="Refresh">
                <IconButton onClick={() => loadResults(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Section Progress Reports">
                <IconButton onClick={() => openProgressReport('section')}
                  sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#c62828', background: '#fff3e0' }}>
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {mode === 'list' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <>
              {/* ── Exam filter bar ── */}
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mr: 1 }}>
                  Filter by Exam:
                </Typography>
                {examTypes.map(et => (
                  <Chip
                    key={et}
                    label={et}
                    onClick={() => { setFilterExam(et); setPage(0); }}
                    sx={{
                      fontWeight: 700, cursor: 'pointer', fontSize: 12,
                      background: filterExam === et ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#e8eaf6',
                      color: filterExam === et ? '#fff' : '#1a237e',
                    }}
                  />
                ))}
                {filterExam !== 'All' && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      {['#', 'Student', 'Exam Type', 'Percentage', 'Rank', 'Remarks', 'Ac. Year', 'Actions'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        {filterExam === 'All'
                          ? `No results for ${selected?.standard} - ${selected?.section}`
                          : `No results for exam: ${filterExam}`}
                      </TableCell></TableRow>
                    ) : paginated.map((r, i) => {
                      const pct = parseFloat(r.percentage || 0);
                      const grade = pct >= 75 ? { bg: '#e8f5e9', color: '#2e7d32' }
                        : pct >= 45 ? { bg: '#fff3e0', color: '#e65100' }
                          : { bg: '#ffebee', color: '#c62828' };
                      return (
                        <TableRow key={r.ID || i} hover>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 30, height: 30, fontSize: 12, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
                                {r.full_name?.[0]}
                              </Avatar>
                              <Typography fontSize={13} fontWeight={600}>{r.full_name || `#${r.student_id}`}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell><Chip label={r.exam_type} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} /></TableCell>
                          <TableCell><Chip label={`${pct}%`} size="small" sx={{ background: grade.bg, color: grade.color, fontWeight: 700 }} /></TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{r.rank || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.remarks || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.ac_year || selected?.acYear}</TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => openResultDetail(r)} sx={{ color: '#1a237e' }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Progress Report">
                              <IconButton size="small" onClick={() => openProgressReport('single', r.student_id)}
                                sx={{ color: '#e65100' }}>
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {deletingId === r.ID ? (
                              <CircularProgress size={16} sx={{ color: '#c62828', ml: 0.5 }} />
                            ) : (
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(r)} sx={{ color: '#c62828' }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filteredResults.length} page={page}
                onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
            </>
          )}
        </Card>
      )}

      {mode === 'add' && (
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <>
              {!selectedStudent ? (
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
                  <Typography fontWeight={700} color="#1a237e" mb={2}>
                    Select Student — {selected?.standard} - {selected?.section}
                  </Typography>
                  <TextField size="small" placeholder="Search student..." value={searchStudent}
                    onChange={e => setSearchStudent(e.target.value)} sx={{ mb: 2, width: 300 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {filteredStudents.map(s => (
                      <Card key={s.ID} onClick={() => setSelectedStudent(s)} sx={{
                        p: 1.5, cursor: 'pointer', borderRadius: 2, border: '1.5px solid #e8eaf6',
                        minWidth: 160, '&:hover': { borderColor: '#1a237e', boxShadow: '0 4px 12px rgba(26,35,126,0.15)' },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 13, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
                            {s.full_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography fontSize={13} fontWeight={600}>{s.full_name}</Typography>
                            <Typography fontSize={11} color="text.secondary">Roll: {s.roll_number || '—'}</Typography>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                    {filteredStudents.length === 0 && (
                      <Typography color="text.secondary">No students found</Typography>
                    )}
                  </Box>
                </Card>
              ) : (
                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
                      {selectedStudent.full_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700} color="#1a237e">{selectedStudent.full_name}</Typography>
                      <Typography fontSize={12} color="text.secondary">Roll: {selectedStudent.roll_number || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography fontWeight={700} color="#1a237e">Max Marks: {totalMaxMarks}, Obtain Marks: {totalObtMarks}, Percentage: {percentageMain}%</Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => setSelectedStudent(null)}
                      sx={{ ml: 'auto', borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>
                      Change Student
                    </Button>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="Exam Type *" value={selectedExam}
                        onChange={e => setSelectedExam(e.target.value)}>
                        <MenuItem value="">— Select Exam —</MenuItem>
                        {exams.map(e => <MenuItem key={e.ID} value={e.exam}>{e.exam}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Overall Percentage" value={percentageMain}
                        onChange={e => setPercentage(e.target.value)} type="number" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Overall Rank" value={rank}
                        onChange={e => setRank(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Remarks" value={remarks}
                        onChange={e => setRemarks(e.target.value)} multiline rows={2} />
                    </Grid>
                  </Grid>

                  {subjectsData.length > 0 && (
                    <Box sx={{ mb: 3, overflowX: 'auto' }}>
                      <Typography fontWeight={700} color="#1a237e" mb={1.5}>Subject-wise Marks</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#f5f6fa' }}>
                            {['Subject', 'Max Marks', 'Min Marks', 'Obt Marks', 'Internals', 'Status', 'Remarks'].map(h => (
                              <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subjectsData.map((s, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{s.subject}</TableCell>
                              {['max_marks', 'min_marks', 'obt_marks', 'internals', 'status', 'remarks'].map(field => (
                                <TableCell key={field} sx={{ p: 0.5 }}>
                                  <TextField size="small" value={s[field]}
                                    onChange={e => updateSubject(idx, field, e.target.value)}
                                    sx={{ width: 90 }}
                                    type={['max_marks', 'min_marks', 'obt_marks', 'internals'].includes(field) ? 'number' : 'text'} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth
                    sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, py: 1.5 }}>
                    {saving ? <CircularProgress size={22} color="inherit" /> : 'Submit Result'}
                  </Button>
                </Card>
              )}
            </>
          )}
        </Box>
      )}

      <Dialog open={!!selectedResult} onClose={() => setSelectedResult(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Result Details — {selectedResult?.full_name} · {selectedResult?.exam_type}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedResult && (() => {
            const pct = parseFloat(selectedResult.percentage || 0);
            const grade = pct >= 75 ? { bg: '#e8f5e9', color: '#2e7d32', label: 'A' }
              : pct >= 45 ? { bg: '#fff3e0', color: '#e65100', label: 'B' }
                : { bg: '#ffebee', color: '#c62828', label: 'F' };
            return (
              <Box>
                <Box sx={{ p: 3, borderRadius: 3, mb: 3, textAlign: 'center', background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
                  <Typography fontSize={52} fontWeight={900} color="white" lineHeight={1}>{pct}%</Typography>
                  <Typography fontSize={14} color="rgba(255,255,255,0.8)" mt={0.5}>Overall Percentage</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    <Chip label={selectedResult.exam_type} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                    {selectedResult.rank && <Chip label={`Rank: ${selectedResult.rank}`} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />}
                    <Chip label={selectedResult.ac_year || selected?.acYear} sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
                    <Chip label={`Grade ${grade.label}`} sx={{ background: grade.bg, color: grade.color, fontWeight: 800 }} />
                  </Box>
                  {selectedResult.remarks && (
                    <Typography fontSize={13} color="rgba(255,255,255,0.75)" mt={1}>{selectedResult.remarks}</Typography>
                  )}
                </Box>

                <Typography fontWeight={700} color="#1a237e" mb={1.5}>Subject-wise Marks</Typography>
                {resultDetailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress sx={{ color: '#1a237e' }} size={28} />
                  </Box>
                ) : resultDetail.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <EmojiEvents sx={{ fontSize: 40, color: '#e8eaf6', mb: 1 }} />
                    <Typography color="text.secondary" fontSize={13}>No subject-wise data available</Typography>
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 480 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: '8px 8px 0 0', px: 2, py: 1.2 }}>
                        {['Subject', 'Max', 'Min', 'Obtained', 'Internals', 'Grade'].map(h => (
                          <Typography key={h} fontSize={12} fontWeight={700} color="white" textAlign="center"
                            sx={{ '&:first-of-type': { textAlign: 'left' } }}>{h}</Typography>
                        ))}
                      </Box>
                      {resultDetail.map((item, idx) => {
                        const obt = parseFloat(item.obt_marks || 0);
                        const min = parseFloat(item.min_marks || 0);
                        const pass = item.status === 'Pass' || obt >= min;
                        return (
                          <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', px: 2, py: 1.2, background: idx % 2 === 0 ? '#fafafa' : 'white', borderBottom: '1px solid #f0f0f0', borderRadius: idx === resultDetail.length - 1 ? '0 0 8px 8px' : 0 }}>
                            <Typography fontSize={13} fontWeight={600} color="#1a237e">{item.subject}</Typography>
                            <Typography fontSize={13} textAlign="center" color="text.secondary">{item.max_marks || '—'}</Typography>
                            <Typography fontSize={13} textAlign="center" color="text.secondary">{item.min_marks || '—'}</Typography>
                            <Typography fontSize={13} textAlign="center" fontWeight={700} color={pass ? '#2e7d32' : '#c62828'}>{item.obt_marks || '—'}</Typography>
                            <Typography fontSize={13} textAlign="center" color="text.secondary">{item.internals || '—'}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Chip label={item.status || (pass ? 'Pass' : 'Fail')} size="small"
                                sx={{ background: pass ? '#e8f5e9' : '#ffebee', color: pass ? '#2e7d32' : '#c62828', fontWeight: 700, fontSize: 10, height: 20 }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setSelectedResult(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
